import { useMemo, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { dateIntervalOptions, getSharedChartOptions } from 'components/reporting/utils';
import { DateInterval, StandardReportInfo } from 'types/reporting';
import { TypedCombobox } from 'types/utils';
import { ReportSpecChart } from 'components/reporting/ReportSpecChart';
import { IntlShape, useIntl } from 'react-intl';
import { OpenAssetButton } from 'components/utils/GoToAssetButton';
import { merge } from 'lodash';
import { AxisLabelsFormatterContextObject, dateFormat, Options } from 'highcharts/highstock';
import { DateIntervalSelect } from 'components/reporting/DateIntervalSelect';

export const chartOptions = (intl: IntlShape, dateInterval?: DateInterval): Options => {
    const dateFormatStr = dateInterval === 'Yr' ? '%Y' : '%Y-%m';
    return merge({}, getSharedChartOptions(intl), {
        exporting: {
            csv: {
                dateFormat: dateFormatStr,
            }
        },
        yAxis: {
            title: {
                text: '$',
            },
            plotLines: [
                {
                    value: 0,
                    color: 'var(--chakra-colors-text-secondary)',
                    width: 1,
                    zIndex: 5,
                },
            ],
        },
        xAxis: {
            type: 'datetime',
            labels: {
                formatter: (context: AxisLabelsFormatterContextObject) => {
                    return dateFormat(dateFormatStr, Number(context.value));
                },
            },
        },
        rangeSelector: {
            enabled: true,
            inputEnabled: false,
            buttons: [{ type: 'all', text: 'All', title: 'View all' }],
            selected: 4,
        },
        legend: {
            enabled: true,
            layout: 'vertical',
            align: 'right',
            verticalAlign: 'middle',
            itemMarginTop: 10,
            itemMarginBottom: 10,
            padding: 40,
        },
        tooltip: {
            shared: false,
            split: false,
            xDateFormat: dateFormatStr,
            pointFormatter: function () {
                if (!!this.y) {
                    const value = Math.round(this.y).toLocaleString('en-US');
                    return `<span style="color:${this.color}">${this.series.name}</span>: <b>$${value}</b><br/>`;
                }
                return '';
            },
        },
        plotOptions: {
            column: {
                stacking: 'normal',
                dataLabels: {
                    enabled: false,
                },
            },
        },
    });
};

interface ProjectNetRevenueProps {
    info: StandardReportInfo;
}

export function NetRevenueBreakdown({ info }: ProjectNetRevenueProps) {
    const intl = useIntl();
    const [selectedInterval, setSelectedInterval] = useState<DateInterval>(dateIntervalOptions?.[0]?.value);
    const [selectedItem, setSelectedItem] = useState<number>(info.items?.[0]?.itemId);

    const studyId = info.studyId;

    const itemTypeId = info.items.find((item) => item.itemId === selectedItem)?.itemTypeId;

    const chartSpec = useMemo(() => {
        return info?.itemTypeCharts?.[String(itemTypeId)];
    }, [info, itemTypeId]);

    const options = useMemo(() => {
        return chartOptions(intl, selectedInterval);
    }, [intl, selectedInterval]);

    return (
        <Flex height={'full'} flexDir={'column'} px={2}>
            <Flex p={4} alignItems="center">
                <DateIntervalSelect value={selectedInterval} onChange={setSelectedInterval} />
                <Box width="lg" ml={4} mr={2}>
                    <TypedCombobox
                        valueKey="itemId"
                        labelKey="name"
                        options={info.items}
                        onChange={(value: number) => setSelectedItem(value)}
                        value={selectedItem}
                        showPlaceholder={false}
                        isControlled={true}
                    />
                </Box>
                {itemTypeId !== undefined && <OpenAssetButton itemId={selectedItem} itemTypeId={itemTypeId} />}
            </Flex>
            <ReportSpecChart
                reportType="PortfolioEnergyAndRev"
                studyId={studyId}
                itemId={selectedItem}
                dateInterval={selectedInterval}
                spec={chartSpec}
                initialOptions={options}
            />
        </Flex>
    );
}
